import {
  LayoutDashboard,
  Briefcase,
  FileText,
  Users,
  CalendarDays,
  Receipt,
  HelpCircle,
  Clock,
  BookOpen,
  MessageSquare,
  MapPin,
  Rocket,
  GraduationCap,
  Route,
  Bot,
  LucideIcon,
} from "lucide-react";
import { OrganizationFeatures, StaffPermissions } from "@/contexts/OrganizationContext";

export interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  requiredFeature?: keyof OrganizationFeatures; // Optional - if undefined, always show
  ownerOnly?: boolean; // Optional - if true, only show to OWNER role
  staffOnly?: boolean; // Optional - if true, only show to STAFF role
  requiresStaffFinancials?: boolean; // Optional - if true, hide from staff when staffCanViewFinancials is false
}

export interface NavGroup {
  label: string;
  items: NavItem[];
  color?: string; // Color theme for the group header
}

// Organized navigation with logical grouping and feature requirements
export const navGroups: NavGroup[] = [
  {
    label: "Today",
    color: "purple",
    items: [
      { name: "AI Agent", href: "/ai-agent", icon: Bot, requiredFeature: "aiAgentEnabled" },
      { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
      { name: "Getting Started", href: "/getting-started", icon: Rocket, ownerOnly: true },
      { name: "Staff Tour", href: "/staff-tour", icon: GraduationCap, staffOnly: true },
    ],
  },
  {
    label: "Work",
    color: "blue",
    items: [
      { name: "Jobs", href: "/jobs", icon: Briefcase },
      { name: "Schedule", href: "/schedule", icon: CalendarDays },
      { name: "Routes", href: "/routes", icon: Route },
      {
        name: "Time Tracking",
        href: "/time-tracking",
        icon: Clock,
        requiredFeature: "timeTrackingEnabled"
      },
      {
        name: "Dispatch",
        href: "/dispatch",
        icon: MapPin,
        requiredFeature: "gpsTrackingEnabled"
      },
    ],
  },
  {
    label: "Customers & Payments",
    color: "green",
    items: [
      { name: "Clients", href: "/clients", icon: Users },
      {
        name: "Quotes",
        href: "/quotes",
        icon: FileText,
        requiresStaffFinancials: true
      },
      {
        name: "Invoices",
        href: "/invoices",
        icon: Receipt,
        requiresStaffFinancials: true
      },
      {
        name: "Service Agreements",
        href: "/service-agreements",
        icon: BookOpen,
        ownerOnly: true
      },
      {
        name: "Messages",
        href: "/messages",
        icon: MessageSquare,
        requiredFeature: "smsEnabled"
      },
    ],
  },
  {
    label: "Help",
    color: "orange",
    items: [
      { name: "Help", href: "/help", icon: HelpCircle },
    ],
  },
];

// Flat list for mobile navigation (no grouping)
export const navItems: NavItem[] = navGroups.flatMap(group => group.items);

/**
 * Filter navigation items based on organization features, user role, and staff permissions
 */
export function filterNavItems(
  items: NavItem[],
  features: OrganizationFeatures,
  staffPermissions: StaffPermissions,
  isOwner: boolean = true,
  isStaff: boolean = false
): NavItem[] {
  return items.filter(item => {
    // Check role requirements
    if (item.ownerOnly && !isOwner) return false;
    if (item.staffOnly && !isStaff) return false;

    // Check staff financial permissions
    if (item.requiresStaffFinancials && isStaff && !staffPermissions.staffCanViewFinancials) {
      return false;
    }

    // If no feature requirement, show (already passed role check)
    if (!item.requiredFeature) return true;

    // Check if the required feature is enabled
    return features[item.requiredFeature] === true;
  });
}

/**
 * Filter navigation groups based on organization features, user role, and staff permissions
 * Returns only groups that have at least one visible item
 */
export function filterNavGroups(
  groups: NavGroup[],
  features: OrganizationFeatures,
  staffPermissions: StaffPermissions,
  isOwner: boolean = true,
  isStaff: boolean = false
): NavGroup[] {
  return groups
    .map(group => ({
      ...group,
      items: filterNavItems(group.items, features, staffPermissions, isOwner, isStaff),
    }))
    .filter(group => group.items.length > 0); // Remove empty groups
}
